import { Injectable } from '@angular/core';

import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Widget, WidgetContext } from '@frontend/sports/types/components/widget';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { isNil } from 'lodash-es';
import { TabItemUiViewModel } from 'packages/sports/libs/common/ui/sports-themed-tab-group/src/lib/tab-group-with-counter/tab-item-view.model';

import { WidgetChevronClickTrackingData } from './tracking.model';

@Injectable()
export class ComposableWidgetTrackingService {
    constructor(
        private trackingService: TrackingService,
        private configAccessor: ModularConfigAccessorService,
    ) {}

    trackComposableNavigation(tabsVM: TabItemUiViewModel): void {
        const trackingData = {
            ...this.getPartialTrackingObject('composable widget navigation', 'click'),
            // TODO: provide correct position event
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.configAccessor.getWidget()?.type ?? NOT_APPLICABLE,
            // TODO: provide correct location event
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getEventLocation() ?? NOT_APPLICABLE,
            // TODO: provide correct event details (title is poor choice because it's translatable string)
            [trackingConstants.COMPONENT_EVENT_DETAILS]: tabsVM?.title ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: tabsVM?.id,
            [trackingConstants.COMPONENT_MODULE_SOURCE]: 'standard module',
        };

        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    trackChildNavigation(widget: Widget<unknown>, widgetContext: Partial<WidgetContext>, itemId: string | undefined, index: number): void {
        const trackingData = {
            ...this.getPartialTrackingObject('composable widget navigation', 'switch widget'),
            [trackingConstants.COMPONENT_POSITION_EVENT]: widget.type.toString(),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(widget.templateName, this.getParamType(widgetContext), itemId),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: widget.templateName,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: index.toString(),
            [trackingConstants.COMPONENT_MODULE_SOURCE]: 'standard module',
            ...widget.trackingData,
        };

        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    trackLinkClick(
        templateName: string | undefined,
        type: string,
        widgetContext: Partial<WidgetContext>,
        itemId: string,
        eventDeatils: string,
        trackingData?: { [key: string]: string },
        url?: string,
    ): void {
        const trackingObject = {
            ...this.getPartialTrackingObject('widget navigation', 'Click'),
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(templateName, this.getParamType(widgetContext), itemId),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDeatils,
            [trackingConstants.COMPONENT_URL_CLICKED]: url ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_MODULE_NAME]: type,
            ...trackingData,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }

    trackAccordionClick(widget: Widget<unknown>, widgetContext: Partial<WidgetContext>, itemId: string): void {
        const trackingData = {
            ...this.getPartialTrackingObject('widget navigation', 'Click'),
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(widget.templateName, this.getParamType(widgetContext), itemId),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'All',
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_MODULE_NAME]: widget.type,
            ...widget.trackingData,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    trackHeaderChevronClick(data: WidgetChevronClickTrackingData): void {
        const trackingData = {
            ...this.getPartialTrackingObject('sports events', 'click'),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getEventLocation() || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_URL_CLICKED]: data.urlClicked || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: data.eventDetails || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    private getParamType(widgetContext: Partial<WidgetContext>): 'competition' | 'sport' | 'affiliation' {
        if (!isNil(widgetContext.competitionId)) {
            return 'competition';
        }

        if (!isNil(widgetContext.sportId)) {
            return 'sport';
        }

        return 'affiliation';
    }

    private getLocation(childTemplate?: string, paramType?: string, id?: string | undefined): string {
        const page = this.configAccessor.getPage();
        const parentTemplate = this.configAccessor.getWidget()?.templateName;

        return [page, parentTemplate, childTemplate, paramType, id]
            .filter((segment) => segment)
            .join('/')
            .toLowerCase();
    }

    private getPartialTrackingObject(labelEvent: string, actionEvent: string): { [key: string]: string } {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: `${this.configAccessor.getPage()?.toLowerCase()} grid widgets`,
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: actionEvent,
        };
    }

    private getEventLocation(): string {
        const page = this.configAccessor.getPage();
        const parentTemplate = this.configAccessor.getWidget()?.templateName;

        return [page, parentTemplate]
            .filter((segment) => segment)
            .join('/')
            .toLowerCase();
    }
}
